import { ComboboxOption } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import useSearchInput from '@/dashboard/hooks/useSearchInput';
import Combobox from '../../Form/components/Combobox';
import { Label } from '@/dashboard/components/Form';
import Button from '../../Button';
import { pagesKey } from '@/dashboard/lib/queryKeys';
import { formatEventDate } from '@/dashboard/lib/format-date';
import PageMultiSelectPageContainer from './PageMultiSelectPageContainer';

const PageMultiSelect = ({
  placeholder = 'Filter by page',
  isSingle = false,
  label,
  helpText,
  name,
  onChange,
  pageTypes = ['checkout', 'event'],
  productTypes = [],
  initialValue,
  includeTicketTypes = false,
}) => {
  const { getPages } = usePageQueries();
  const { sellerId } = useAuth();
  const [selectedPages, setSelectedPages] = useState(new Map());
  const [pageTicketSelections, setPageTicketSelections] = useState(new Map());
  const { setSearchInput, search } = useSearchInput('');
  const hasInitialized = useRef(false);

  const initialPageIds =
    initialValue && Array.isArray(initialValue) ? initialValue.map(v => v.pageId) : [];

  const { data: initialPagesData } = useQuery({
    queryKey: ['initial-pages', initialPageIds],
    queryFn: () =>
      getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        productType: productTypes,
        _id: initialPageIds,
      }),
    enabled: initialPageIds.length > 0 && selectedPages.size === 0,
  });

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: [pagesKey, search],
    queryFn: async ({ pageParam }) =>
      await getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        productType: productTypes,
        limit: 10,
        skip: pageParam * 10,
        search: search === '' ? undefined : search,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const addSelectedPage = useCallback(
    pageToAdd => {
      if (selectedPages.has(pageToAdd._id)) return;
      setSelectedPages(prev => {
        const newMap = new Map(prev);
        newMap.set(pageToAdd._id, { ...pageToAdd });
        return newMap;
      });
    },
    [selectedPages],
  );

  const clear = useCallback(() => {
    setSelectedPages(new Map());
    setPageTicketSelections(new Map());
    setSearchInput('');
  }, [setSelectedPages, setPageTicketSelections, setSearchInput]);

  const removeSelectedPage = pageToRemove => {
    setSelectedPages(prev => {
      const newMap = new Map(prev);
      newMap.delete(pageToRemove._id);
      return newMap;
    });

    setPageTicketSelections(prev => {
      const newMap = new Map(prev);
      newMap.delete(pageToRemove._id);
      return newMap;
    });
  };

  const getFilteredPages = () => {
    if (!data?.pages) return data?.pages;

    return data.pages.map(pageGroup => ({
      ...pageGroup,
      data: pageGroup.data.filter(page => !selectedPages.has(page._id)),
    }));
  };

  const handlePageSelectionChange = useCallback(
    page => {
      if (page == null) {
        clear();
        return;
      }
      if (isSingle) {
        setSelectedPages(new Map());
        setPageTicketSelections(new Map());
      }
      addSelectedPage(page);
      setSearchInput('');
    },
    [isSingle, clear, addSelectedPage, setSearchInput],
  );

  const handleTicketTypeSelectionChange = useCallback((pageId, selectedTicketTypeIds) => {
    setPageTicketSelections(prev => {
      if (JSON.stringify(prev.get(pageId)) === JSON.stringify(selectedTicketTypeIds)) {
        return prev;
      }

      const newMap = new Map(prev);
      newMap.set(pageId, selectedTicketTypeIds);
      return newMap;
    });
  }, []);

  const openDashboardLink = pageId => {
    window.open(`/dashboard/pages/${pageId}/customize/product`, '_blank');
  };

  /**
   * Initialize data for initialValues. Data comes from a separate `useQuery`
   */
  useEffect(() => {
    if (initialPagesData?.data && selectedPages.size === 0 && hasInitialized.current === false) {
      const newSelectedPages = new Map();
      const newPageTicketSelections = new Map();

      initialPagesData.data.forEach(page => {
        const initialItem = initialValue.find(item => item.pageId === page._id);
        if (initialItem) {
          newSelectedPages.set(page._id, page);
          if (initialItem.ticketTypeIds && initialItem.ticketTypeIds.length > 0) {
            newPageTicketSelections.set(page._id, initialItem.ticketTypeIds);
          }
        }
      });

      setSelectedPages(newSelectedPages);
      setPageTicketSelections(newPageTicketSelections);
      hasInitialized.current = true;
    }
  }, [initialPagesData, selectedPages.size, initialValue]);

  useEffect(() => {
    onChange({
      pages: selectedPages,
      tickets: pageTicketSelections,
    });
  }, [selectedPages, pageTicketSelections, onChange]);

  return (
    <div>
      {label && <Label>{label}</Label>}

      <Combobox
        name={name}
        immediate
        value={null}
        onChange={value => {
          handlePageSelectionChange(value);
        }}
        onClear={e => {
          e.preventDefault();
          clear();
        }}
        onChangeInput={e => setSearchInput(e.target.value)}
        placeholder={placeholder}
        hasItems={getFilteredPages()?.[0]?.data?.length > 0}
        isError={isError}
        isLoading={isLoading}
        noItemsMsg="No pages found"
        fetchNextPage={fetchNextPage}
        showSearchIcon={true}
        autocomplete="off"
      >
        {getFilteredPages()?.map(data =>
          data?.data.map(page => {
            return (
              <ComboboxOption
                key={page._id}
                value={page}
                className={({ focus }) => `cursor-pointer relative ${focus ? 'bg-gray-100' : ''}`}
              >
                <div
                  key={page._id}
                  type="button"
                  className={clsx(
                    'flex w-full p-2 hover:bg-gray-100 transition-colors border-b border-gray-100',
                  )}
                >
                  <div>
                    <div className="text-xs text-gray-400 capitalize">{page.type}</div>
                    <div className="text-sm font-semibold truncate">{page.name}</div>
                    {page.type === 'checkout' && (
                      <div className="text-gray-700">
                        <span>{getProductPriceLabel(page.product)}</span>
                      </div>
                    )}
                    {page.type === 'event' && page.eventDetails.startDate && (
                      <div className="text-gray-700">
                        <div className="text-gray-700 text-sm">
                          <div className="flex items-center gap-x-1 justify-start">
                            <CalendarIcon className="h-4 w-4" />

                            <div className="flex flex-col font-medium">
                              {formatEventDate({
                                startDate: page.eventDetails.startDate,
                                endDate: page.eventDetails.endDate,
                                timezone: page.eventDetails.timezone,
                                locale: page.locale,
                              }).map(date => (
                                <span key={date}>{date}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex ml-auto">
                    <Button
                      appearance="icon-button"
                      onClick={() => openDashboardLink(page._id)}
                      type="button"
                      onMouseDown={e => e.preventDefault()}
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                </div>
              </ComboboxOption>
            );
          }),
        )}
      </Combobox>

      <div className="block text-gray-500 font-normal text-xs pointer font-medium mt-1">
        {helpText}
      </div>

      {selectedPages.size > 0 && (
        <div className="mt-2">
          {Array.from(selectedPages.values()).map((page, index, array) => (
            <PageMultiSelectPageContainer
              key={page._id}
              page={page}
              isLast={index === array.length - 1}
              onRemove={removeSelectedPage}
              onVisit={openDashboardLink}
              showRemoveButton={true}
              includeTicketTypes={includeTicketTypes}
              selectedTicketIds={pageTicketSelections.get(page._id) || []}
              onTicketTypeChange={selectedTicketIds => {
                handleTicketTypeSelectionChange(page._id, selectedTicketIds);
              }}
              isReadOnly={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PageMultiSelect;
